import { useEffect, useState } from "react";
import { getCandidateOffer } from "../../services/candidateApi";
import { LEGAL_ENTITIES, localToday } from "../../constants/employmentTerms";
import { formatCompensation, parseCompensationInput } from "../../utils/compensation";

export default function HireCandidateModal({ open, candidate, busy = false, onClose, onSubmit }) {
    const [dateHired, setDateHired] = useState(localToday);
    const [legalEntity, setLegalEntity] = useState(LEGAL_ENTITIES[0]);
    const [offer, setOffer] = useState(null);
    const [offerLoading, setOfferLoading] = useState(false);
    const [offerError, setOfferError] = useState("");
    const [legacyTerms, setLegacyTerms] = useState({ salary: "", position: "", work_schedule: "" });

    useEffect(() => {
        if (!open || !candidate?.id) return;
        setDateHired(localToday());
        setLegalEntity(LEGAL_ENTITIES[0]);
        setOffer(null);
        setOfferError("");
        setLegacyTerms({ salary: "", position: candidate.last_vacancy_title || candidate.position || "", work_schedule: "5/2" });
        setOfferLoading(true);
        let active = true;
        Promise.resolve(getCandidateOffer(candidate.id))
            .then((data) => {
                if (!active) return;
                setOffer(data);
                if (data) {
                    setDateHired(data.employment_date || localToday());
                    setLegalEntity(data.legal_entity || LEGAL_ENTITIES[0]);
                }
            })
            .catch((error) => {
                if (!active) return;
                if (error?.status === 404) setOffer(null);
                else setOfferError(error?.message || "Не удалось загрузить оффер");
            })
            .finally(() => { if (active) setOfferLoading(false); });
        return () => { active = false; };
    }, [open, candidate?.id, candidate?.last_vacancy_title, candidate?.position]);

    if (!open || !candidate) return null;

    const submit = async (event) => {
        event.preventDefault();
        const compensation = offer
            ? { salary: offer.salary, salary_terms: offer.salary_terms }
            : parseCompensationInput(legacyTerms.salary);
        const position = offer?.position ?? legacyTerms.position.trim();
        const workSchedule = offer?.work_schedule ?? legacyTerms.work_schedule.trim();
        if (!dateHired || !legalEntity || !compensation || !position || !workSchedule || busy || offerError) return;
        await onSubmit?.({
            date_hired: dateHired,
            legal_entity: legalEntity,
            ...compensation,
            position,
            work_schedule: workSchedule,
        });
    };

    return (
        <div
            className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/55 p-4 backdrop-blur-sm"
            role="presentation"
            onMouseDown={(event) => {
                if (event.target === event.currentTarget && !busy) onClose?.();
            }}
        >
            <form
                onSubmit={submit}
                role="dialog"
                aria-modal="true"
                aria-labelledby="hire-candidate-title"
                className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-slate-900/10"
            >
                <div className="border-b border-slate-100 bg-gradient-to-br from-emerald-50 to-white px-6 py-5">
                    <div className="flex items-start gap-4">
                        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-emerald-600 text-white shadow-sm">
                            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                            </svg>
                        </div>
                        <div>
                            <h2 id="hire-candidate-title" className="text-xl font-bold text-slate-900">
                                Добавить в сотрудники
                            </h2>
                            <p className="mt-1 text-sm text-slate-600">
                                {candidate.full_name || "Кандидат"}
                            </p>
                        </div>
                    </div>
                </div>

                <div className="space-y-4 px-6 py-5">
                    <p className="text-sm leading-6 text-slate-600">Проверьте согласованные условия. Зарплата, должность и график будут перенесены из оффера.</p>
                    {offerLoading ? <div className="rounded-xl bg-slate-50 p-4 text-sm text-slate-500">Загружаем условия оффера…</div> : null}
                    {offerError ? <div role="alert" className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">{offerError}. Закройте окно и повторите попытку.</div> : null}
                    {!offerLoading && offer ? <div className="grid gap-3 rounded-xl border border-emerald-100 bg-emerald-50 p-4 sm:grid-cols-2">
                        <div><span className="block text-xs font-semibold uppercase text-emerald-700">Оплата</span><strong className="text-slate-900">{formatCompensation(offer)}</strong></div>
                        <div><span className="block text-xs font-semibold uppercase text-emerald-700">Должность</span><strong className="text-slate-900">{offer.position}</strong></div>
                        <div><span className="block text-xs font-semibold uppercase text-emerald-700">График</span><strong className="text-slate-900">{offer.work_schedule}</strong></div>
                        <div><span className="block text-xs font-semibold uppercase text-emerald-700">Дата из оффера</span><strong className="text-slate-900">{offer.employment_date}</strong></div>
                    </div> : null}
                    {!offerLoading && !offer && !offerError ? <div className="space-y-3 rounded-xl border border-amber-200 bg-amber-50 p-4"><p className="text-sm text-amber-900">Это старый кандидат: сохранённый оффер не найден. Заполните условия вручную один раз.</p><div className="grid gap-3 sm:grid-cols-2"><input aria-label="Итоговая зарплата" type="text" maxLength={1000} placeholder="Сумма или условия оплаты" value={legacyTerms.salary} onChange={(e) => setLegacyTerms((v) => ({ ...v, salary: e.target.value }))} className="rounded-xl border border-amber-300 px-3 py-2" /><input aria-label="Должность" placeholder="Должность" value={legacyTerms.position} onChange={(e) => setLegacyTerms((v) => ({ ...v, position: e.target.value }))} className="rounded-xl border border-amber-300 px-3 py-2" /><input aria-label="График работы" placeholder="График" value={legacyTerms.work_schedule} onChange={(e) => setLegacyTerms((v) => ({ ...v, work_schedule: e.target.value }))} className="rounded-xl border border-amber-300 px-3 py-2 sm:col-span-2" /></div></div> : null}
                    <label className="block">
                        <span className="text-sm font-semibold text-slate-800">Дата выхода на работу</span>
                        <input
                            type="date"
                            required
                            autoFocus
                            value={dateHired}
                            onChange={(event) => setDateHired(event.target.value)}
                            disabled={busy}
                            data-testid="candidate-hire-date"
                            className="mt-2 w-full rounded-xl border border-slate-300 bg-white px-3.5 py-2.5 text-slate-900 outline-none transition focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100 disabled:bg-slate-100"
                        />
                    </label>
                    <label className="block">
                        <span className="text-sm font-semibold text-slate-800">Юридическое лицо</span>
                        <select aria-label="Юридическое лицо" required value={legalEntity} onChange={(event) => setLegalEntity(event.target.value)} disabled={busy || offerLoading} className="mt-2 w-full rounded-xl border border-slate-300 bg-white px-3.5 py-2.5">
                            {LEGAL_ENTITIES.map((item) => <option key={item}>{item}</option>)}
                        </select>
                    </label>
                    <div className="rounded-xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-xs leading-5 text-emerald-900">
                        После подтверждения кандидат получит статус «Вышел на работу» и появится в разделе «Сотрудники».
                    </div>
                </div>

                <div className="flex justify-end gap-3 border-t border-slate-100 bg-slate-50 px-6 py-4">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={busy}
                        className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-50"
                    >
                        Отмена
                    </button>
                    <button
                        type="submit"
                        disabled={busy || offerLoading || Boolean(offerError) || !dateHired || !legalEntity || (!offer && (!parseCompensationInput(legacyTerms.salary) || !legacyTerms.position.trim() || !legacyTerms.work_schedule.trim()))}
                        className="rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        {busy ? "Добавляем…" : "Добавить в сотрудники"}
                    </button>
                </div>
            </form>
        </div>
    );
}
